import React, { useEffect, useState } from "react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import { toast } from "sonner";
import { CreditCard, Check, X, Zap } from "lucide-react";
import YanbuOfferBanner from "./YanbuOfferBanner";

const PLAN_COLORS = {
  free: "#525252",
  basic: "#8a6f2e",
  plus: "#c9a55c",
  pro: "#0a0a0a",
  enterprise: "#1f1614",
};

export default function DashSubscription({ businessId }) {
  const { lang } = useI18n();
  const [ctx, setCtx] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p, i] = await Promise.all([
        apiClient.get(`/platform/plans/business/${businessId}/current`),
        apiClient.get("/platform/plans/list"),
        apiClient.get(`/platform/plans/business/${businessId}/invoices`),
      ]);
      setCtx(c.data);
      setPlans(p.data.items);
      setInvoices(i.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [businessId]);

  const change = async (plan_key, billing_period) => {
    setChanging(plan_key);
    try {
      await apiClient.post(`/platform/plans/business/${businessId}/change`, {
        plan_key, billing_period, payment_method: "manual",
      });
      toast.success(lang === "ar" ? "تم تغيير الخطة" : "Plan changed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setChanging(null); }
  };

  const cancel = async () => {
    if (!window.confirm(lang === "ar" ? "إلغاء الاشتراك؟" : "Cancel subscription?")) return;
    try {
      await apiClient.post(`/platform/plans/business/${businessId}/cancel`);
      toast.success(lang === "ar" ? "تم الإلغاء" : "Canceled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading || !ctx) return <div className="text-[#8a8a8a]">…</div>;
  const currentPlan = ctx.plan || {};
  const sub = ctx.subscription || {};
  const usage = ctx.usage || {};
  const color = PLAN_COLORS[currentPlan.key] || "#525252";

  return (
    <div className="space-y-6" data-testid="dash-subscription">
      {/* Yanbu Showrooms exclusive 3-month offer (iter143) — visible only
          to eligible dealer businesses (category=dealer, city=Yanbu,
          verification_status=verified). Server-enforced. */}
      <YanbuOfferBanner businessId={businessId} subscription={sub} onActivated={load} />

      {/* Current plan hero */}
      <div className="rounded-2xl p-6 border border-[#0a0a0a]/[0.06]" style={{ background: "linear-gradient(135deg,#fff,#f7f4ea)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: color, color: "#fff" }}>
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">{lang === "ar" ? "الخطة الحالية" : "Current plan"}</div>
            <div className="text-2xl font-bold" style={{ color }}>{lang === "ar" ? currentPlan.name_ar : currentPlan.name_en}</div>
          </div>
          {sub.status && (
            <span className="text-[11px] font-bold px-3 py-1 rounded-full" style={{ background: sub.status === "active" ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)", color: sub.status === "active" ? "#16a34a" : "#dc2626" }}>
              {sub.status}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-4">
          <Row labelAr="الدورة" labelEn="Billing" value={sub.billing_period || "—"} />
          <Row labelAr="بدأت" labelEn="Started" value={sub.started_at?.slice(0, 10) || "—"} />
          <Row labelAr="تنتهي/تجدد" labelEn="Renews" value={sub.renews_at?.slice(0, 10) || (lang === "ar" ? "دون تجديد" : "—")} />
          <Row labelAr="السعر" labelEn="Price"
               value={`${(sub.billing_period === "yearly" ? currentPlan.price_yearly_sar : currentPlan.price_monthly_sar) || 0} SAR`} />
        </div>
        {sub.status === "active" && sub.plan_key !== "free" && (
          <button type="button" onClick={cancel} className="text-[12px] text-red-600 mt-4 hover:underline" data-testid="btn-cancel-sub">
            {lang === "ar" ? "إلغاء الاشتراك" : "Cancel subscription"}
          </button>
        )}
      </div>

      {/* Usage vs limits */}
      <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
          {lang === "ar" ? "الاستخدام مقابل الحدود" : "Usage vs limits"}
        </div>
        <div className="space-y-3">
          <UsageBar labelAr="إعلانات" labelEn="Listings" used={usage.listings} limit={currentPlan.limits?.max_listings} />
          <UsageBar labelAr="إعلانات مميّزة" labelEn="Featured" used={usage.featured_listings} limit={currentPlan.limits?.max_featured_listings} />
          <UsageBar labelAr="موظفون" labelEn="Employees" used={usage.employees} limit={currentPlan.limits?.max_employees} />
          <UsageBar labelAr="فروع" labelEn="Branches" used={usage.branches} limit={currentPlan.limits?.max_branches} />
        </div>
      </div>

      {/* Available plans */}
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
          {lang === "ar" ? "الخطط المتاحة" : "Available plans"}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.key}
                 className={`rounded-2xl p-5 border transition-all ${p.key === currentPlan.key ? "border-[#c9a55c] shadow-md" : "border-[#0a0a0a]/[0.06] bg-white"}`}
                 style={p.key === currentPlan.key ? { background: "linear-gradient(135deg,#fff,#faf5e8)" } : {}}
                 data-testid={`plan-card-${p.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-lg font-bold" style={{ color: PLAN_COLORS[p.key] }}>{lang === "ar" ? p.name_ar : p.name_en}</div>
                {p.key === currentPlan.key && <Check className="w-4 h-4 text-[#c9a55c]" />}
              </div>
              {p.description_ar && <div className="text-[12px] text-[#8a8a8a] mb-3">{lang === "ar" ? p.description_ar : p.description_en}</div>}
              <div className="text-2xl font-bold text-[#0a0a0a] mb-3">{p.price_monthly_sar} <span className="text-[11px] font-normal text-[#8a8a8a]">SAR/mo</span></div>
              <ul className="space-y-1 text-[12px] text-[#525252] mb-4">
                <FeatureLine on ok={p.limits.max_listings === -1 || p.limits.max_listings > 0}
                             text={p.limits.max_listings === -1 ? (lang === "ar" ? "إعلانات غير محدودة" : "Unlimited listings") : `${p.limits.max_listings} ${lang === "ar" ? "إعلان" : "listings"}`} />
                <FeatureLine ok={p.limits.max_featured_listings !== 0} text={`${p.limits.max_featured_listings === -1 ? "∞" : p.limits.max_featured_listings} ${lang === "ar" ? "مميّز" : "featured"}`} />
                <FeatureLine ok={p.features.analytics} text={lang === "ar" ? "تحليلات متقدمة" : "Advanced analytics"} />
                <FeatureLine ok={p.features.priority_support} text={lang === "ar" ? "دعم أولوية" : "Priority support"} />
                <FeatureLine ok={p.features.api_access} text={lang === "ar" ? "وصول للـAPI" : "API access"} />
              </ul>
              {p.key !== currentPlan.key ? (
                <div className="flex gap-2">
                  <button type="button" disabled={!!changing} onClick={() => change(p.key, "monthly")}
                          className="flex-1 py-2 rounded-full text-[12px] font-bold disabled:opacity-50"
                          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                          data-testid={`btn-switch-${p.key}`}>
                    {changing === p.key ? "…" : (lang === "ar" ? "شهري" : "Monthly")}
                  </button>
                  <button type="button" disabled={!!changing} onClick={() => change(p.key, "yearly")}
                          className="flex-1 py-2 rounded-full text-[12px] font-bold border border-[#0a0a0a] text-[#0a0a0a] disabled:opacity-50">
                    {lang === "ar" ? "سنوي (وفّر)" : "Yearly (save)"}
                  </button>
                </div>
              ) : (
                <div className="text-center text-[12px] text-[#c9a55c] font-bold py-2">{lang === "ar" ? "خطتك الحالية" : "Current plan"}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="invoices-list">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
            {lang === "ar" ? "الفواتير" : "Invoices"} ({invoices.length})
          </div>
          <div className="divide-y divide-[#0a0a0a]/[0.06]">
            {invoices.map((inv) => (
              <div key={inv.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold font-mono">{inv.invoice_number}</div>
                  <div className="text-[11px] text-[#8a8a8a]">{inv.kind} · {inv.issued_at?.slice(0, 10)}</div>
                </div>
                <div className="text-end">
                  <div className="font-bold">{inv.total_sar} SAR</div>
                  <div className={`text-[11px] font-semibold ${inv.status === "paid" ? "text-green-700" : "text-orange-600"}`}>{inv.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center text-[11px] text-[#8a8a8a] pt-4">
        <Zap className="w-3 h-3 inline me-1" />
        {lang === "ar" ? "ادفع لاحقًا بأمان — Mada, Apple Pay قريبًا" : "Pay securely later — Mada, Apple Pay coming soon"}
      </div>
    </div>
  );
}

function Row({ labelAr, labelEn, value }) {
  const { lang } = useI18n();
  return (
    <div className="rounded-lg bg-white/60 px-3 py-2">
      <div className="text-[10px] uppercase font-semibold text-[#8a8a8a]">{lang === "ar" ? labelAr : labelEn}</div>
      <div className="text-sm font-bold text-[#0a0a0a] mt-0.5">{value}</div>
    </div>
  );
}

function UsageBar({ labelAr, labelEn, used = 0, limit = 0 }) {
  const { lang } = useI18n();
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const danger = pct >= 90;
  return (
    <div>
      <div className="flex items-center justify-between text-[12px] mb-1">
        <span className="text-[#525252]">{lang === "ar" ? labelAr : labelEn}</span>
        <span className="font-semibold text-[#0a0a0a] font-mono">
          {used} / {isUnlimited ? "∞" : limit}
        </span>
      </div>
      <div className="h-2 rounded-full bg-[#f0eee7] overflow-hidden">
        <div className="h-full transition-all" style={{ width: isUnlimited ? "100%" : `${pct}%`, background: isUnlimited ? "linear-gradient(90deg,#e8c873,#c9a55c)" : danger ? "#dc2626" : "linear-gradient(90deg,#e8c873,#8a6f2e)" }} />
      </div>
    </div>
  );
}

function FeatureLine({ ok, text }) {
  return (
    <li className="flex items-center gap-1.5">
      {ok ? <Check className="w-3.5 h-3.5 text-green-600" /> : <X className="w-3.5 h-3.5 text-[#8a8a8a]" />}
      <span className={ok ? "" : "line-through text-[#8a8a8a]"}>{text}</span>
    </li>
  );
}
